import React, { useEffect, useMemo, useRef, useState } from "react";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import CardGiftcardOutlinedIcon from "@mui/icons-material/CardGiftcardOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import GroupAddOutlinedIcon from "@mui/icons-material/GroupAddOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import logo from "assets/images/Dr_Hoffmann_Logo_f1.svg";
import LeonardoLanguageToggle from "components/LeonardoLanguageToggle";
import { useLeonardoLocale } from "scenes/leonardoNikolai/LeonardoLocaleContext";
import { formatPrice } from "constants/massages";
import { buildAuthHref } from "utils/registration";
import { readShopBasket, SHOP_BASKET_EVENT, SHOP_BASKET_KEY } from "utils/shopBasket";

const NAV_CURVE_LAYOUT = {
  home: { y: 15, angle: 2.8, slope: 0.26, bend: 0.08 },
  referral: { y: 28, angle: 1.2, slope: 0.12, bend: 0.05 },
  kontakt: { y: 36, angle: 0.8, slope: 0.08, bend: 0.04 },
  events: { y: 37, angle: 0.65, slope: 0.07, bend: 0.04 },
  shop: { y: 38, angle: 0.5, slope: 0.06, bend: 0.03 },
  admin: { y: 40, angle: 0.35, slope: 0.05, bend: 0.03 },
};

const CurvedNavLabel = ({ config }) => {
  const chars = Array.from(config.label);

  return (
    <span className="nav-curve-text" aria-hidden="true">
      {chars.map((char, index) => {
        return (
          <span
            className="nav-curve-char"
            key={`${config.label}-${index}-${char}`}
            style={{
              "--char-y": `${config.y}px`,
              "--char-angle": "0deg",
            }}
          >
            {char}
          </span>
        );
      })}
    </span>
  );
};

const BalanceTopUpLink = ({ user, className = "" }) => {
  if (!user) return null;
  const balanceCents = Math.max(0, Number(user.referralBalanceCents || 0));

  return (
    <Link
      to="/aufladen"
      className={`nav-balance-link ${className}`.trim()}
      aria-label={`Kontoguthaben ${formatPrice(balanceCents)} aufladen`}
    >
      <span className="nav-balance-copy">
        <span className="nav-balance-label">Guthaben</span>
        <strong>{formatPrice(balanceCents)}</strong>
      </span>
      <span className="nav-balance-plus" aria-hidden="true">
        <AddOutlinedIcon />
      </span>
    </Link>
  );
};

const NavCartLink = ({ className = "" }) => {
  const [count, setCount] = useState(() => readShopBasket().length);
  const [bumping, setBumping] = useState(false);
  const countRef = useRef(count);
  const bumpTimer = useRef(null);

  useEffect(() => {
    const sync = (items) => {
      const next = Array.isArray(items) ? items.length : readShopBasket().length;
      if (next > countRef.current) {
        setBumping(true);
        window.clearTimeout(bumpTimer.current);
        bumpTimer.current = window.setTimeout(() => setBumping(false), 650);
      }
      countRef.current = next;
      setCount(next);
    };
    const onChange = (event) => sync(event.detail);
    const onStorage = (event) => {
      if (event.key === SHOP_BASKET_KEY) sync(readShopBasket());
    };
    window.addEventListener(SHOP_BASKET_EVENT, onChange);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(SHOP_BASKET_EVENT, onChange);
      window.removeEventListener("storage", onStorage);
      window.clearTimeout(bumpTimer.current);
    };
  }, []);

  const label = count === 1 ? "Warenkorb, 1 Artikel" : `Warenkorb, ${count} Artikel`;

  return (
    <Link
      to="/warenkorb"
      className={`nav-cart-link ${className} ${bumping ? "is-added" : ""}`.trim()}
      aria-label={label}
    >
      <ShoppingCartOutlinedIcon aria-hidden="true" />
      {count > 0 && <span className="nav-cart-count">{count}</span>}
    </Link>
  );
};

const Navbar = ({ renderNavLanguageToggle = null }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(() =>
    typeof window !== "undefined" ? window.scrollY > 24 : false
  );
  const accountMenuRef = useRef(null);
  const location = useLocation();
  const user = useSelector((s) => s.user);
  const { shellT, isLeonardoPage } = useLeonardoLocale();

  const navCurve = useMemo(
    () => ({
      home: { label: shellT("shell.nav.home"), ...NAV_CURVE_LAYOUT.home },
      referral: { label: shellT("shell.nav.referral"), ...NAV_CURVE_LAYOUT.referral },
      kontakt: { label: shellT("shell.nav.contact"), ...NAV_CURVE_LAYOUT.kontakt },
      events: { label: shellT("shell.nav.events"), ...NAV_CURVE_LAYOUT.events },
      shop: { label: shellT("shell.nav.shop"), ...NAV_CURVE_LAYOUT.shop },
      admin: { label: shellT("shell.nav.admin"), ...NAV_CURVE_LAYOUT.admin },
    }),
    [shellT]
  );

  useEffect(() => {
    document.body.classList.toggle("menu-open", mobileOpen);
    return () => document.body.classList.remove("menu-open");
  }, [mobileOpen]);

  useEffect(() => {
    let frameId = null;

    const updateScrollState = () => {
      frameId = null;
      setIsScrolled(window.scrollY > 24);
    };

    const onScroll = () => {
      if (frameId !== null) return;
      frameId = window.requestAnimationFrame(updateScrollState);
    };

    updateScrollState();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);

    return () => {
      if (frameId !== null) window.cancelAnimationFrame(frameId);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [location.pathname, location.hash]);

  useEffect(() => {
    if (!accountOpen) return undefined;

    const onPointerDown = (event) => {
      if (!accountMenuRef.current?.contains(event.target)) {
        setAccountOpen(false);
      }
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setAccountOpen(false);
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [accountOpen]);

  const closeMenu = () => setMobileOpen(false);
  const isHomePage = location.pathname === "/";
  const navClassName = [
    isScrolled ? "scrolled" : "",
    isHomePage && !isScrolled && !mobileOpen && !accountOpen ? "is-hidden-at-top" : "",
  ].filter(Boolean).join(" ");

  return (
    <>
      <nav className={navClassName}>
        {isLeonardoPage ? (
          <span className="nav-logo" aria-label="Dr. Hoffmann">
            <img src={logo} alt="Dr. Hoffmann" />
          </span>
        ) : (
          <Link to="/" className="nav-logo" aria-label="Dr. Hoffmann" onClick={closeMenu}>
            <img src={logo} alt="Dr. Hoffmann" />
          </Link>
        )}
        <ul className="nav-links">
          <li>
            <Link to="/empfehlungsprogramm" className="nav-curve-link nav-referral-link" aria-label={navCurve.referral.label}>
              <CurvedNavLabel config={navCurve.referral} />
            </Link>
          </li>
          <li>
            <Link to="/kontakt" className="nav-curve-link" aria-label={navCurve.kontakt.label}>
              <CurvedNavLabel config={navCurve.kontakt} />
            </Link>
          </li>
          <li>
            <Link to="/events" className="nav-curve-link" aria-label={navCurve.events.label}>
              <CurvedNavLabel config={navCurve.events} />
            </Link>
          </li>
          <li>
            <Link to="/shop" className="nav-curve-link" aria-label={navCurve.shop.label}>
              <CurvedNavLabel config={navCurve.shop} />
            </Link>
          </li>
          {user ? (
            user.isAdmin && (
              <li>
                <NavLink to="/admin-dashboard" className="nav-curve-link nav-admin-link" aria-label={navCurve.admin.label}>
                  <CurvedNavLabel config={navCurve.admin} />
                </NavLink>
              </li>
            )
          ) : null}
          <li className="nav-actions">
            {renderNavLanguageToggle?.() || (isLeonardoPage && <LeonardoLanguageToggle />)}
            <NavCartLink className="nav-cart-link--desktop" />
            <BalanceTopUpLink user={user} className="nav-balance-link--desktop" />
            <Link to="/buchen" className="nav-cta">{shellT("shell.nav.bookCta")}</Link>
            {user ? (
              <div className="nav-account-menu" ref={accountMenuRef}>
                <button
                  type="button"
                  className="nav-login-icon"
                  aria-label={shellT("shell.nav.accountMenu")}
                  aria-haspopup="menu"
                  aria-expanded={accountOpen}
                  title={shellT("shell.nav.myAccount")}
                  onClick={() => setAccountOpen((open) => !open)}
                >
                  <AccountCircleOutlinedIcon aria-hidden="true" />
                </button>
                <div className={`nav-account-dropdown ${accountOpen ? "open" : ""}`} role="menu">
                  <Link to="/konto" role="menuitem" onClick={() => setAccountOpen(false)}>
                    <PersonOutlineOutlinedIcon aria-hidden="true" />
                    {shellT("shell.nav.myAccount")}
                  </Link>
                  <Link to="/konto/termine" role="menuitem" onClick={() => setAccountOpen(false)}>
                    <EventOutlinedIcon aria-hidden="true" />
                    {shellT("shell.nav.myAppointments")}
                  </Link>
                  <Link to="/konto/gutscheine" role="menuitem" onClick={() => setAccountOpen(false)}>
                    <CardGiftcardOutlinedIcon aria-hidden="true" />
                    {shellT("shell.nav.vouchers")}
                  </Link>
                  <Link to="/konto/empfehlen" role="menuitem" onClick={() => setAccountOpen(false)}>
                    <GroupAddOutlinedIcon aria-hidden="true" />
                    {shellT("shell.nav.referFriends")}
                  </Link>
                </div>
              </div>
            ) : (
              <div className="nav-account-menu" ref={accountMenuRef}>
                <button
                  type="button"
                  className="nav-login-icon"
                  aria-label={shellT("shell.nav.loginMenu")}
                  aria-haspopup="menu"
                  aria-expanded={accountOpen}
                  title={shellT("shell.nav.login")}
                  onClick={() => setAccountOpen((open) => !open)}
                >
                  <AccountCircleOutlinedIcon aria-hidden="true" />
                </button>
                <div className={`nav-account-dropdown ${accountOpen ? "open" : ""}`} role="menu">
                  <Link to={buildAuthHref("/einloggen")} role="menuitem" onClick={() => setAccountOpen(false)}>
                    <PersonOutlineOutlinedIcon aria-hidden="true" />
                    {shellT("shell.nav.login")}
                  </Link>
                  <Link to={buildAuthHref("/registrieren")} role="menuitem" onClick={() => setAccountOpen(false)}>
                    <AccountCircleOutlinedIcon aria-hidden="true" />
                    {shellT("shell.nav.register")}
                  </Link>
                </div>
              </div>
            )}
          </li>
        </ul>
        <div className="nav-mobile-controls">
          {renderNavLanguageToggle?.()}
          {isLeonardoPage && (
            <LeonardoLanguageToggle className="leonardo-lang-toggle--nav-mobile" />
          )}
          <NavCartLink className="nav-cart-link--mobile" />
          <BalanceTopUpLink user={user} className="nav-balance-link--mobile" />
          {!isLeonardoPage && (
            <div
              className={`hamburger ${mobileOpen ? "active" : ""}`}
              role="button"
              aria-label={shellT("shell.nav.openMenu")}
              aria-expanded={mobileOpen}
              tabIndex={0}
              onClick={() => setMobileOpen((v) => !v)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setMobileOpen((v) => !v);
                }
              }}
            >
              <span></span><span></span><span></span>
            </div>
          )}
        </div>
      </nav>

      <div className={`mobile-menu ${mobileOpen ? "open" : ""}`}>
        <Link to="/empfehlungsprogramm" onClick={closeMenu}>{shellT("shell.nav.referral")}</Link>
        <Link to="/kontakt" onClick={closeMenu}>{shellT("shell.nav.contact")}</Link>
        <Link to="/events" onClick={closeMenu}>{shellT("shell.nav.events")}</Link>
        <Link to="/shop" onClick={closeMenu}>{shellT("shell.nav.shop")}</Link>
        {user ? (
          <>
            <Link to="/konto" onClick={closeMenu}>{shellT("shell.nav.myAccount")}</Link>
            <Link to="/konto/termine" onClick={closeMenu}>{shellT("shell.nav.myAppointments")}</Link>
            <Link to="/konto/gutscheine" onClick={closeMenu}>{shellT("shell.nav.vouchers")}</Link>
            {user.isAdmin && (
              <Link to="/admin-dashboard" className="mobile-admin-link" onClick={closeMenu}>{shellT("shell.nav.admin")}</Link>
            )}
          </>
        ) : (
          <Link to={buildAuthHref("/einloggen")} onClick={closeMenu}>{shellT("shell.nav.login")}</Link>
        )}
        <Link to="/buchen" onClick={closeMenu} className="btn-primary">
          {shellT("shell.nav.bookCta")}
        </Link>
      </div>
    </>
  );
};

export default Navbar;
